import { api } from './api';
import { getLocalAppSession } from 'utils/storageHelpers';
import { VideoTemplateStyle } from 'components/video-gen/types';
import { LogoPlace } from 'types/adEditorTypes';

const customerHeader = () => ({
  'Active-Customer': getLocalAppSession()?.activeCustomer,
});

/**
 * SYM-6163. How long the browser waits for an answer on the video calls that
 * do their work *inside* the request.
 *
 * Every call in the app shares one client with a 60 second ceiling
 * (`api/api.ts`). That number was sized for ordinary screens, where a slow
 * answer means something is wrong and failing fast is the kindness. Three of
 * the video endpoints are not that: they run one or two OpenAI calls, build a
 * preview composition, or upscale a photo before they answer, and the customer
 * is deliberately kept waiting while it happens. On those, hanging up at 60
 * seconds does not report a failure — it *invents* one, on work the server
 * goes on to finish.
 *
 * Measured on production 2026-09-07: a preview-config on 2026-05-22 (customer
 * 22765) left 8 minutes between the assistant's row and the draft it produced,
 * against 6-7 seconds on the same day's other requests. That was the day the
 * Sentry issue first fired.
 *
 * Three minutes is a ceiling, not a target. It applies per call, and only to
 * the calls below — polling and every other request in the app still fail
 * fast, unchanged.
 */
export const VIDEO_SYNC_REQUEST_TIMEOUT_MS = 180_000; // 3 min

/**
 * Request options for a video call that does its work before answering.
 * Anything that merely queues a job, or polls, must NOT use this — a queue
 * insert that takes a minute is a fault worth surfacing, not waiting out.
 */
const slowVideoRequestConfig = () => ({
  headers: customerHeader(),
  timeout: VIDEO_SYNC_REQUEST_TIMEOUT_MS,
});

// ---------------------------------------------------------------------------
// Image crop (percent). Shape mirrors react-image-crop, but pinned to `%` so a
// crop is never ambiguous about its unit at the API boundary. Stored on a
// variant as `crop`. Sent to the BE as the Bring-to-Life focus rectangle; the
// BE is authoritative for fitting it to the target output aspect.
// ---------------------------------------------------------------------------

export interface PercentCrop {
  unit: '%';
  x: number;
  y: number;
  width: number;
  height: number;
}

// ---------------------------------------------------------------------------
// POST /v4/video/preview-config
// ---------------------------------------------------------------------------

/**
 * SYM-6206. How one layer of the preview moves into place.
 *
 * The browser must draw the SAME curve ffmpeg does, or the preview lies about
 * timing (`OverlayFilterBuilder::position` and `::progress` in sympl-api):
 *
 *     p        = min(max((t - starts_at) / duration, 0), 1)
 *     progress = easing === 'quadratic-out' ? (1 - p) ** 2 : 1 - p
 *     x(t)     = x + distance * progress
 */
export interface PreviewPlanAnimation {
  duration: number;
  /**
   * Pixels, and already signed: negative starts left of where it lands.
   *
   * ⚠️ Resolved by the server on purpose. The distance is a multiple of the
   * element's OWN drawn width, which is only known after the text has been
   * measured -- a browser working it out from a percentage would fly short
   * words in from the wrong place.
   */
  distance: number;
  direction: string;
  easing: string;
}

/** One picture the renderer composites, and the window it is on screen for. */
export interface PreviewPlanLayer {
  name: string;
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
  starts_at: number;
  /** null means it stays to the last frame. */
  ends_at: number | null;
  /** null for a layer that simply appears, such as the brand wash. */
  animation: PreviewPlanAnimation | null;
}

/**
 * SYM-6221. The slow drift across a photo on the Dynamic look.
 *
 * The photo -- after the customer's crop, and after it has been fitted to cover
 * the frame -- grows from `scale_from` at the slide's own start to `scale_to`
 * at its end, in a straight line, about `origin`. The origin is a point on the
 * FRAME as fractions of its width and height, which the server aims at the
 * people in the photo. ffmpeg draws the very same numbers.
 */
export interface PreviewPlanDrift {
  scale_from: number;
  scale_to: number;
  origin: { x: number; y: number };
}

export interface PreviewPlanSlide {
  url: string;
  /** Fractions of the photo (0-1), not percentages. Null keeps the whole photo. */
  crop: { x: number; y: number; width: number; height: number } | null;
  start: number;
  duration: number;
  /** Null on every look but Dynamic: the photo stays still. */
  drift?: PreviewPlanDrift | null;
  /**
   * How this slide arrives. A cut is one frame of fade placed one frame early,
   * which is what ffmpeg draws, so the browser must not treat it as instant.
   */
  transition: {
    name: string;
    duration: number;
    starts_at: number;
    is_cut: boolean;
  };
}

/**
 * Everything needed to play a preview of a video our own renderer will make.
 *
 * The layers are listed back to front, in the order they composite -- and they
 * are LAYERS, not named element types: a style that adds a new kind of picture
 * arrives here without the player needing to know what it is.
 */
export interface PreviewPlan {
  width: number;
  height: number;
  duration: number;
  fps: number;
  slides: PreviewPlanSlide[];
  layers: PreviewPlanLayer[];
  audio_url: string;
}

export interface PreviewConfigRequest {
  vacancy_id: number;
  format: string;
  /**
   * SYM-6197. Pictures the customer picked for this video.
   *
   * A campaign has no picture pool: a video is built from the pictures on the
   * campaign's ads plus the brand's default creatives, so somebody with a full
   * media library and no ads is refused one while owning plenty of pictures.
   * These are added in front of what the campaign offers, never instead of it,
   * and three distinct pictures are still the minimum.
   */
  image_resource_ids?: number[];
  video?: {
    provider?: string;
    template_id?: string;
    render_style?: VideoTemplateStyle;
    include_logo?: boolean;
    use_vacancy_first_slide_title?: boolean;
    placements?: string[];
  };
}

export interface PreviewSlide {
  index: number;
  image_url: string;
  headline: string;
  subtext?: string;
  /**
   * SYM-6215. True when the customer set this slide's length by hand. Stored
   * with the draft and sent back on every update, so picking a style later
   * leaves the slide's length alone.
   */
  duration_set_by_hand?: boolean;
  /** SYM-6265. True when the customer chose this slide's transition by hand. */
  transition_set_by_hand?: boolean;
}

/**
 * SYM-6215. The pace one style stands for, in seconds. The server owns these
 * numbers; the wizard applies them and never keeps a copy of its own.
 */
export interface StylePace {
  slide_duration: number;
  transition_duration: number;
  /**
   * SYM-6265. How this look's slides change, by position: the first name is
   * how slide 2 arrives. Empty or missing means the look has no fixed order.
   */
  transitions?: string[];
}

export interface PreviewConfig {
  provider: string;
  render_style: VideoTemplateStyle;
  /**
   * SYM-6265. Where the logo sits, for the whole video. SYM-6353: the four
   * places of the shared logo row, top middle included.
   */
  logo_position?: LogoPlace;
  format: string;
  template_id?: string;
  slides: PreviewSlide[];
}

export interface PreviewConfigResponse {
  draft_id: number;
  thread_id: string;
  provider: string;
  /**
   * @deprecated BE-owned legacy OpenAI config. FE should read from
   * `preview_payload` instead. Per Ihar 2026-04-17, this field will be
   * removed in a future iteration.
   */
  config: PreviewConfig;
  /**
   * Full Creatomate composition. Pass directly to Preview SDK
   * via `preview.setSource(preview_payload)`. Do not modify
   * before sending to the SDK.
   */
  preview_payload: Record<string, any>;
  /**
   * SYM-6206. The moving preview for a video OUR renderer will make.
   *
   * Present only for a draft recorded as `modal`; a Creatomate draft sends
   * nothing here and the Creatomate player keeps driving the pane.
   *
   * ⚠️ The browser draws NOTHING of its own from this. Every picture in it --
   * each line of text, the brand wash, the blocks behind the text, the logo --
   * was drawn by the same code that composites the video, so line breaks, font
   * sizes, colours and positions cannot differ between preview and render. The
   * player places the pictures and plays the timeline, nothing more.
   */
  preview_plan?: PreviewPlan | null;
  /**
   * SYM-6196. The numbers the server holds the video to, so the wizard can
   * hold the customer to the same ones instead of keeping its own copy:
   * `max_slide_characters` is the budget for one slide's text, the same one
   * the model is given, and `max_slides` is how many slides are kept.
   *
   * Optional: a server that has not shipped this yet sends nothing, and the
   * wizard falls back to the same defaults the server has.
   */
  limits?: {
    max_slide_characters?: number | null;
    max_slides?: number | null;
    /**
     * SYM-6215. The pace each style stands for. Picking a style sets every
     * slide the customer has not touched to its `slide_duration`.
     *
     * Optional: a server that has not shipped this yet sends nothing, and
     * then picking a style does not touch any slide's length.
     */
    styles?: Partial<Record<VideoTemplateStyle, StylePace>> | null;
    /** SYM-6220. The style a new draft starts on. */
    default_style?: VideoTemplateStyle | null;
  } | null;
}

export const fetchPreviewConfig = async (
  req: PreviewConfigRequest
): Promise<PreviewConfigResponse> => {
  // Runs the config assistant, then a second assistant pass, then builds the
  // Creatomate composition — all before it answers.
  const { data } = await api.post(
    '/v4/video/preview-config',
    req,
    slowVideoRequestConfig()
  );
  return data.data;
};

// ---------------------------------------------------------------------------
// GET /v4/video/drafts/{draftId}/style-examples — Track 1, SYM-6226
// ---------------------------------------------------------------------------

/**
 * SYM-6226. One little moving example of one look.
 *
 * `plan` is the very same shape a draft's `preview_plan` carries, and it is not
 * a coincidence: the server builds it with the same plan builder, from a
 * one-slide video in that look, on this draft's own picture, colour and logo.
 * So the wizard plays it with the player it already has and lays out nothing of
 * its own -- which is the whole point, and SYM-6179's lesson.
 */
export interface StyleExample {
  style: VideoTemplateStyle;
  plan: PreviewPlan;
}

export interface StyleExamplesResponse {
  /** What all three examples are actually drawn in. */
  brand_colour: string;
  /**
   * False means the colour above is OUR navy, not the customer's: their brand
   * holds no colour of its own. The step has to say so, or the customer reads
   * navy as theirs.
   */
  brand_has_own_colour: boolean;
  /**
   * The campaign's brand, so the step's "set your brand colour" link opens
   * that brand in the settings rather than the list of all of them (Steven,
   * 23 Sep 2026). Null when the campaign has no brand.
   */
  brand_id?: number | null;
  /** Always dynamic, professional, minimal -- in that order. */
  examples: StyleExample[];
}

/**
 * All three looks in ONE call, which is the server's decision and its reasons
 * are worth keeping here: the three must agree on the picture (the frames
 * differ by the look and by nothing else), the picture-choosing is the slow
 * part and would otherwise be paid for three times, and one call has one
 * failure rather than seven ways of half-succeeding.
 */
export const fetchStyleExamples = async (
  draftId: number
): Promise<StyleExamplesResponse> => {
  const { data } = await api.get(`/v4/video/drafts/${draftId}/style-examples`, {
    headers: customerHeader(),
  });
  return data.data;
};

// ---------------------------------------------------------------------------
// POST /v4/video/reopen — Track 1, SYM-6185
// ---------------------------------------------------------------------------

/**
 * Open the video that is on an ad again. The server finds what that file was
 * made from and hands back a NEW draft holding it, in the shape
 * `preview-config` answers with, so the wizard carries on as it always does.
 * It asks the model nothing. 404 means there is nothing behind the file: it was
 * uploaded, or made before drafts were kept.
 */
export const reopenVideo = async (
  resourceId: number
): Promise<PreviewConfigResponse> => {
  const { data } = await api.post(
    '/v4/video/reopen',
    { resource_id: resourceId },
    { headers: customerHeader() }
  );
  return data.data;
};

// ---------------------------------------------------------------------------
// POST /v4/video/generate — Track 1 (Creatomate, from draft)
// ---------------------------------------------------------------------------

export interface GenerateVideoDraftRequest {
  draft_id: number;
}

export interface GenerateVideoAsyncResponse {
  ai_generation_id?: number;
  status: string;
  provider: string;
  thread_id: string;
  draft_id: number;
  // Present when backend responds synchronously with a completed video
  id?: number;
  path?: string;
  thumbnail_path?: string;
}

export const generateVideo = async (
  req: GenerateVideoDraftRequest
): Promise<GenerateVideoAsyncResponse> => {
  // Answers with a queued job for a Creatomate draft, but the endpoint still
  // holds a branch that renders the video and uploads it before answering —
  // which is why this response type carries a finished video at all.
  const { data } = await api.post(
    '/v4/video/generate',
    req,
    slowVideoRequestConfig()
  );
  return data.data;
};

// ---------------------------------------------------------------------------
// POST /v4/video/generate — Track 2 (fal.ai / Hailuo)
// fal.ai is not yet deployed to QA — FAL_MOCK_MODE stays true until it is.
// ---------------------------------------------------------------------------

export interface GenerateVideoFalRequest {
  vacancy_id: number;
  format: string;
  video: {
    provider: 'fal';
    source_image_url: string; // HTTPS URL of the image to animate (fallback)
    // SYM-5564: id of the source CustomerResource + the user's focus rectangle.
    // When both are present the BE crops the resource to the focus, fits the
    // target output aspect, and animates that derivative instead of the raw URL.
    source_resource_id?: number;
    crop?: PercentCrop;
    prompt?: string;
    model_id?: string;
    overlay_preset_id?: string;
    include_logo?: boolean;
    placements?: string[];
    /**
     * SYM-6353. The ad the clip is made from, so the server can take the
     * words the image ad shows as the suggestions for the ones on the video.
     */
    ad_variant_id?: number;
    /** SYM-6353. The look drawn over the clip. Left out means dynamic. */
    render_style?: VideoTemplateStyle;
    /** SYM-6353. Where the logo sits. Left out means top right. */
    logo_position?: LogoPlace;
    /**
     * SYM-6353. ONLY the words the customer changed or emptied. A key that is
     * here is used as sent, "" included (= that line is hidden); a key that
     * is not here means "use the suggestion". See BtlTexts.
     */
    texts?: BtlTexts;
  };
}

// ---------------------------------------------------------------------------
// POST /v4/video/btl/preview — Bring to Life, SYM-6353
// ---------------------------------------------------------------------------

/** The three words drawn on a Bring to Life clip. */
export type BtlWordKey = 'callout' | 'title' | 'location';

/**
 * SYM-6353. The words for a Bring to Life clip, as the customer left them.
 *
 * PRESENT MEANS USED, ABSENT MEANS THE SUGGESTION. A key that is here is drawn
 * exactly as sent, and "" means that line is hidden. A key that is not here
 * means "use what the image ad shows". So the app sends only the words the
 * customer changed or emptied: a word left alone stays the ad's own, even if
 * the ad changes before the clip is drawn.
 */
export type BtlTexts = Partial<Record<BtlWordKey, string>>;

export interface BtlPreviewRequest {
  vacancy_id: number;
  ad_variant_id?: number;
  format: string;
  /** The same picture and crop /v4/video/generate is sent. */
  source_resource_id?: number;
  source_image_url?: string;
  crop?: PercentCrop;
  texts?: BtlTexts;
  logo_position?: LogoPlace;
}

export interface BtlPreviewResponse {
  /**
   * What the image ad itself shows: its own callout (else the brand's), its
   * job title, and the targeting places written the templates' way.
   */
  suggestions: Record<BtlWordKey, string>;
  brand_colour: string;
  /** False means the colour is OUR navy: the brand holds none of its own. */
  brand_has_own_colour: boolean;
  brand_id?: number | null;
  /** One plan per look, drawn on the ad's picture with the words and logo. */
  examples: StyleExample[];
}

/**
 * SYM-6353. The three looks drawn on the ad's own picture, with the words and
 * the logo place as they are on the screen, plus the words the image ad shows.
 * The same drawing Track 1's style examples use, asked for without a draft.
 */
/**
 * How long a preview request may take before it counts as failed. The shared
 * client waits 60 s, which is what a customer saw on QA (27 Sep 2026): a
 * request that hung after the network came back kept "Building…" on screen
 * for a minute. A preview is drawn in a few seconds when it works at all.
 */
export const BTL_PREVIEW_TIMEOUT_MS = 15_000;

export const fetchBtlPreview = async (
  req: BtlPreviewRequest,
  options: { signal?: AbortSignal } = {}
): Promise<BtlPreviewResponse> => {
  const { data } = await api.post('/v4/video/btl/preview', req, {
    headers: customerHeader(),
    timeout: BTL_PREVIEW_TIMEOUT_MS,
    signal: options.signal,
  });
  // Every v4 video answer so far is wrapped in `data`; the contract for this
  // one names only the body, so either shape is read.
  return data?.data?.suggestions ? data.data : data;
};

export const FAL_MOCK_MODE = import.meta.env.VITE_VIDEO_MOCK === 'true';

const MOCK_FAL_RESPONSE: GenerateVideoAsyncResponse = {
  ai_generation_id: -1,
  status: 'pending',
  provider: 'falai',
  thread_id: 'mock-fal-thread',
  draft_id: -1,
};

export const generateVideoFal = async (
  req: GenerateVideoFalRequest
): Promise<GenerateVideoAsyncResponse> => {
  if (FAL_MOCK_MODE) {
    await new Promise((r) => setTimeout(r, 500));
    return MOCK_FAL_RESPONSE;
  }
  // Queues the fal job today, so it answers in milliseconds. It stops being a
  // quick call the moment SYM-6135 deploys: a photo below 1080px is sent to
  // fal's Clarity upscaler inside this request, on purpose, because the
  // customer is waiting for it.
  const { data } = await api.post(
    '/v4/video/generate',
    req,
    slowVideoRequestConfig()
  );
  return data.data;
};

// ---------------------------------------------------------------------------
// POST /v4/video/update — user edits after generation (draft-based)
// Send config (slide reorder) OR feedback (text edit) — never both.
// ---------------------------------------------------------------------------

export interface UpdateVideoSlideReorderRequest {
  draft_id: number;
  config: {
    provider: string;
    format: string;
    render_style?: VideoTemplateStyle;
    /**
     * SYM-6265. One choice for the whole video; left out means top right.
     * SYM-6353 added top middle, the fourth place of the image-ad row.
     */
    logo_position?: LogoPlace;
    template_id?: string;
    slides: Array<{
      image: {
        index: number;
        // Set when the user replaces the slide's image via the customer
        // resource picker. Per SYM-5409, the BE normalizes this into the
        // draft asset set and overrides `index` resolution. Omitted for
        // unchanged or AI-suggested images.
        resource_id?: number;
        // SYM-6272. null means "this slide has no crop" and must be SENT: the
        // server keeps every key an update leaves out, which is how a swapped
        // picture inherited the previous picture's crop.
        crop?: {
          unit: string;
          x: number;
          y: number;
          width: number;
          height: number;
        } | null;
      };
      // A string, or the first slide's banner word + job line as the draft stores it.
      text: string | Record<string, unknown>;
      subtext?: string;
      duration?: number;
      /** SYM-6215. Sent for every slide, so the server keeps it with the draft. */
      duration_set_by_hand?: boolean;
      /** SYM-6265. Sent with every slide, like the one above. */
      transition_set_by_hand?: boolean;
      transition?: 'fade' | 'dissolve' | 'slide' | 'cut';
    }>;
  };
}

export interface UpdateVideoFeedbackRequest {
  draft_id: number;
  feedback: string;
}

export type UpdateVideoRequest =
  | UpdateVideoSlideReorderRequest
  | UpdateVideoFeedbackRequest;

export interface UpdateVideoResponse {
  draft_id: number;
  thread_id: string;
  config: PreviewConfig;
  /**
   * Fresh Creatomate composition reflecting the user's edits. When present,
   * feed straight to the Preview SDK via `preview.setSource(preview_payload)`.
   */
  preview_payload?: Record<string, any> | null;
  /**
   * SYM-6206. A fresh plan after an edit, for a draft our own renderer will
   * make. Absent for a Creatomate draft.
   */
  preview_plan?: PreviewPlan | null;
}

export const updateVideo = async (
  req: UpdateVideoRequest
): Promise<UpdateVideoResponse> => {
  // A text edit re-runs the assistant on the draft's thread before answering.
  const { data } = await api.post(
    '/v4/video/update',
    req,
    slowVideoRequestConfig()
  );
  return data.data;
};

// ---------------------------------------------------------------------------
// GET /v4/video/generations/{id}
// ---------------------------------------------------------------------------

export type GenerationStatus =
  | 'pending'
  | 'submitted'
  | 'processing'
  | 'webhook_received'
  | 'quality_check'
  | 'overlay_rendering'
  | 'succeeded'
  | 'formats_generating'
  | 'completed'
  | 'partial_complete'
  | 'failed'
  | 'blocked';

export interface RenderedResource {
  id: number;
  path: string;
  thumbnail_path: string;
  thread_id: string;
  width: number;
  height: number;
  created_at: string;
}

export interface GenerationStatusResponse {
  ai_generation_id: number;
  status: GenerationStatus;
  provider: string;
  thread_id: string;
  format: string;
  error_message: string | null;
  completed_at: string | null;
  rendered_resource_id: number | null;
  rendered_resource: RenderedResource | null;
}

export const fetchGenerationStatus = async (
  generationId: number
): Promise<GenerationStatusResponse> => {
  const { data } = await api.get(`/v4/video/generations/${generationId}`, {
    headers: customerHeader(),
  });
  return data.data;
};
